"use client"

/**
 * PushNotificationInjector — real browser Push Notifications (ExoClick
 * "Direct"/self-hosted method). Unlike every other ad placement in this
 * codebase, this CANNOT be sandboxed: registering a service worker for our
 * own origin requires the network's script to run directly, unsandboxed, in
 * our top-level document — the same trust level as loading any other
 * <script> tag, not the "cannot reach outside its own frame no matter how
 * it behaves" guarantee PopunderInjector/InterstitialAd/etc. rely on. That
 * tradeoff was discussed and accepted explicitly before this was built.
 *
 * Requires public/worker.js to exist at the site root (it does — see that
 * file's own comment for where its content came from) and triggers a real
 * browser permission prompt the first time it runs. Fundamentally a
 * one-time ask, not a repeating ad: gated on a plain "have we ever asked"
 * localStorage flag, not the frequency/cooldown system every other
 * placement uses (there's no "show it again in N minutes" for a
 * subscription prompt — once dismissed or answered, asking again on the
 * next page load would just be annoying).
 */

import { useEffect, useRef } from "react"
import { isAdFreePath } from "@/lib/ad-placement"
import { useAuthStore } from "@/lib/auth-store"
import { hasPremiumAccess } from "@/lib/entitlements"
import { useHasConsent } from "@/components/cookie-consent"
import { isAdTestModeOn } from "@/lib/ad-cooldown"
import { useAdTriggerStore } from "@/lib/ad-trigger-store"

const ASKED_KEY = "goonru_push_notif_asked"
const SKIP_ROLES = ["admin", "super_admin", "moderator"]

function injectScripts(embedCode: string) {
  if (document.querySelector("[data-push-notif-injected]")) return
  const marker = document.createElement("meta")
  marker.setAttribute("data-push-notif-injected", "1")
  document.head.appendChild(marker)

  // Real <script> elements, created via the DOM API and appended in order —
  // script tags written via innerHTML/insertAdjacentHTML never execute, so
  // the embed_code string (an inline vars block + an external src) has to
  // be parsed apart and rebuilt as actual elements.
  const doc = new DOMParser().parseFromString(embedCode, "text/html")
  doc.querySelectorAll("script").forEach((old) => {
    const s = document.createElement("script")
    s.type = old.type || "text/javascript"
    if (old.src) s.src = old.src
    else s.textContent = old.textContent
    document.body.appendChild(s)
  })
}

export function PushNotificationInjector() {
  const { profile, isLoading } = useAuthStore()
  const hasConsent = useHasConsent()
  const injected = useRef(false)
  // AdTestPanel's manual trigger — see the matching comment on this counter
  // in lib/ad-trigger-store.ts. Without it, this component only ever fires
  // once per browser (the ASKED_KEY flag below), so an admin testing it a
  // second time had to clear localStorage and reload by hand.
  const triggerCount = useAdTriggerStore((s) => s.pushNotification)
  const lastTriggerRef = useRef(0)

  useEffect(() => {
    const manuallyTriggered = triggerCount > lastTriggerRef.current
    lastTriggerRef.current = triggerCount
    if ((injected.current && !manuallyTriggered) || isLoading || !hasConsent) return
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return

    // Goonpals, commissions and settings never get an ad, including this
    // browser permission prompt.
    if (!manuallyTriggered && isAdFreePath(window.location.pathname)) return

    const testMode = isAdTestModeOn()
    if (!testMode && !manuallyTriggered) {
      if (hasPremiumAccess(profile as any)) return
      if (profile?.role && SKIP_ROLES.includes(profile.role)) return
      try {
        if (localStorage.getItem(ASKED_KEY) === "1") return
      } catch {}
    }

    if (manuallyTriggered) {
      // Let a real re-test actually re-run the injection instead of
      // silently no-op'ing against the "already injected this session"
      // marker inside injectScripts().
      document.querySelector("[data-push-notif-injected]")?.remove()
      injected.current = false
    }

    fetch(`/api/ads/network-grid-ad?type=push_notifications&tier=3${testMode || manuallyTriggered ? "&preview=1" : ""}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d?.display_js) return
        injected.current = true
        try { localStorage.setItem(ASKED_KEY, "1") } catch {}
        injectScripts(d.display_js)
      })
      .catch(() => {})
  }, [isLoading, hasConsent, profile, triggerCount])

  return null
}
